import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Alert, LinearProgress,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmailIcon from '@mui/icons-material/Email';

const SUPPORT_EMAIL = 'info@motorsportsuite.com';

export default function AiQuotaExceededDialog({ open, onClose, quota }) {
  const { t } = useTranslation('race_engineer');
  if (!quota) return null;

  const handleContact = () => {
    const subject = encodeURIComponent('Upgrade piano per analisi AI esaurite');
    const body = encodeURIComponent(
      `Ciao,\n\nHo esaurito le analisi AI del mio piano (${quota.planLabel}, ${quota.used}/${quota.limit}).\n` +
      `Vorrei valutare un upgrade per continuare a usare il Race Engineer AI.\n\nGrazie,\n`
    );
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <AutoAwesomeIcon color="warning" />
        {t('quota_exceeded.title', { defaultValue: 'Analisi AI esaurite' })}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {quota.isTrial
            ? t('quota_exceeded.trial_body', {
                defaultValue: 'Hai usato tutte le {{limit}} analisi AI incluse nel periodo di prova.',
                limit: quota.limit,
              })
            : t('quota_exceeded.plan_body', {
                defaultValue: 'Hai esaurito le {{limit}} analisi AI del tuo piano {{plan}} per questo mese.',
                limit: quota.limit,
                plan: quota.planLabel,
              })}
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('quota_exceeded.usage_label', { defaultValue: 'Utilizzo' })}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {quota.used} / {quota.limit}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={100} color="error" sx={{ height: 6, borderRadius: 3 }} />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {quota.isTrial
            ? t('quota_exceeded.trial_cta', {
                defaultValue:
                  'Per continuare a usare il Race Engineer AI, contattaci per attivare un piano a pagamento. ' +
                  'I tuoi dati e setup rimarranno disponibili: pagherai solo la quota mensile dal momento dell\'attivazione.',
              })
            : t('quota_exceeded.plan_cta', {
                defaultValue:
                  'Per ricevere altre analisi questo mese, contattaci per fare upgrade a un piano superiore o aggiungere un pacchetto extra.',
              })}
        </Typography>

        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>{t('quota_exceeded.contact_label', { defaultValue: 'Email di contatto:' })}</strong>
            {' '}{SUPPORT_EMAIL}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>
          {t('quota_exceeded.dismiss', { defaultValue: 'Chiudi' })}
        </Button>
        <Button variant="contained" startIcon={<EmailIcon />} onClick={handleContact}>
          {t('quota_exceeded.contact', { defaultValue: 'Contattaci' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
